import { useLayoutEffect, useRef, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import type { FormEffect, FormCopy } from '../types/block';
import type { Compute } from '../types/compute';
import { evaluateCompute } from '../evaluators/computeEvaluator';
import { evaluateCondition } from '../evaluators/conditionEvaluator';
import type { ConditionContext } from '../evaluators/conditionEvaluator';
import { INPUT_FORMAT_FNS } from './inputFormatters';
import type { FormPrecisionConfig } from '../precision/precisionContract';
import { normalizeProgrammaticNumericValue } from '../precision/precisionContract';
import {
  isProgrammaticWrite,
  markProgrammaticWrites,
  releaseProgrammaticWrites,
  type ProgrammaticWriteGuardRef,
} from './programmaticWriteGuard';

const FORMAT_FNS = INPUT_FORMAT_FNS;

interface UseEffectProcessorParams {
  form: UseFormReturn<Record<string, unknown>>;
  effects: FormEffect[];
  meta: Record<string, unknown>;
  getOptionProp: (field: string, prop: string) => unknown;
  customResolvers?: Record<string, (ctx: ConditionContext) => unknown>;
  hydratingRef?: React.RefObject<boolean>;
  hydrationEpochRef?: React.RefObject<number>;
  programmaticWriteGuard?: ProgrammaticWriteGuardRef;
  precisionConfig?: FormPrecisionConfig;
}

export function useEffectProcessor({
  form,
  effects,
  meta,
  getOptionProp,
  customResolvers,
  hydratingRef,
  hydrationEpochRef,
  programmaticWriteGuard,
  precisionConfig,
}: UseEffectProcessorParams): void {
  const watchFields = useMemo(() => collectWatchFields(effects), [effects]);
  const watchedValues = useWatch({ control: form.control, name: watchFields });
  const prevValuesRef = useRef<Record<string, unknown>>({});
  const prevEpochRef = useRef(hydrationEpochRef?.current ?? 0);
  const isInitialRef = useRef(true);

  useLayoutEffect(() => {
    const effectWrittenFields: string[] = [];
    try {
    const watchMap: Record<string, unknown> = {};
    watchFields.forEach((field, idx) => {
      watchMap[field] = Array.isArray(watchedValues) ? watchedValues[idx] : watchedValues;
    });

    if (isInitialRef.current) {
      isInitialRef.current = false;
      prevValuesRef.current = { ...watchMap };

      const runOnMountEffects = effects.filter((e) => e.runOnMount);
      if (runOnMountEffects.length > 0) {
        const currentValues = form.getValues();
        for (const effect of runOnMountEffects) {
      if (!isEffectEnabled(effect, currentValues)) continue;
          const watchNames = Array.isArray(effect.watch) ? effect.watch : [effect.watch];
          const hasValue = watchNames.some((name) => watchMap[name] != null && watchMap[name] !== '');
          const conditionChecksAbsence = effect.condition != null && (
            'notEquals' in effect.condition || 'isEmpty' in effect.condition || 'not' in effect.condition
          );
          if (!hasValue && !conditionChecksAbsence) continue;

          const conditionMet = evaluateEffectCondition(effect, watchMap, currentValues, meta, getOptionProp, customResolvers);
          if (!conditionMet) continue;

          applySet(effect, watchMap, currentValues, form, meta, getOptionProp, customResolvers, precisionConfig);
          applyCopy(effect.copy, currentValues, form);
          applyFormat(effect, watchMap, form);
          if (effect.action === 'mutualExclude' && effect.fields?.length) {
            applyMutualExclude(effect, watchMap, form);
          }
        }
      }
      return;
    }

    if (hydratingRef?.current) {
      prevValuesRef.current = { ...watchMap };
      return;
    }

    if (hydrationEpochRef && hydrationEpochRef.current !== prevEpochRef.current) {
      prevValuesRef.current = { ...watchMap };
      prevEpochRef.current = hydrationEpochRef.current;
      return;
    }

    const currentValues = form.getValues();

    for (const effect of effects) {
      if (!isEffectEnabled(effect, currentValues)) continue;

      const watchNames = Array.isArray(effect.watch) ? effect.watch : [effect.watch];
      const hasChanged = watchNames.some(
        (name) => watchMap[name] !== prevValuesRef.current[name],
      );

      if (!hasChanged) continue;

      const blockedByGuard = watchNames.some((name) => isProgrammaticWrite(programmaticWriteGuard, name));

      if (blockedByGuard) {
        continue;
      }

      const conditionMet = evaluateEffectCondition(effect, watchMap, currentValues, meta, getOptionProp, customResolvers);

      if (!conditionMet) {
        if (effect.copy && (effect.copy.clearOnReverse ?? true)) {
          clearCopyTargets(effect.copy, form);
        }
        continue;
      }

      if (effect.action && !KNOWN_EFFECT_ACTIONS.has(effect.action)) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(
            `[form-engine] Unknown effect action "${effect.action}" on watch "${effect.watch}". ` +
            `Known actions: ${Array.from(KNOWN_EFFECT_ACTIONS).join(', ')}`,
          );
        }
      }

      if (effect.action === 'mutualExclude' && effect.fields?.length) {
        applyMutualExclude(effect, watchMap, form);
      }

      if (effect.clear) {
        for (const fieldName of effect.clear) {
          form.setValue(fieldName, null, { shouldDirty: false });
          effectWrittenFields.push(fieldName);
        }
      }

      if (effect.appendIfEmpty) {
        applyAppendIfEmpty(effect.appendIfEmpty, form, currentValues, meta, getOptionProp);
      }

      const setFields = applySet(effect, watchMap, currentValues, form, meta, getOptionProp, customResolvers, precisionConfig);
      effectWrittenFields.push(...setFields);

      applyCopy(effect.copy, currentValues, form);
      applyFormat(effect, watchMap, form);
    }

    prevValuesRef.current = { ...watchMap };
    } finally {
      releaseProgrammaticWrites(programmaticWriteGuard);
      if (effectWrittenFields.length > 0) {
        markProgrammaticWrites(programmaticWriteGuard, effectWrittenFields);
      }
    }
  }, [watchedValues]);
}

const KNOWN_EFFECT_ACTIONS = new Set([
  'mutualExclude',
  'clear',
  'set',
  'copy',
  'format',
  'appendIfEmpty',
]);

/* ─── Helpers ───────────────────────────────────────────────────── */

function evaluateEffectCondition(
  effect: FormEffect,
  watchMap: Record<string, unknown>,
  currentValues: Record<string, unknown>,
  meta: Record<string, unknown>,
  getOptionProp: (field: string, prop: string) => unknown,
  customResolvers?: Record<string, (ctx: ConditionContext) => unknown>,
): boolean {
  if (!effect.condition) return true;
  const watchNames = Array.isArray(effect.watch) ? effect.watch : [effect.watch];
  const ctx: ConditionContext = {
    fieldValue: watchMap[watchNames[0]],
    values: currentValues,
    meta,
    getOptionProp,
    customResolvers: customResolvers as Record<string, (ctx: ConditionContext) => boolean>,
  };
  return evaluateCondition(effect.condition, ctx);
}

function applySet(
  effect: FormEffect,
  watchMap: Record<string, unknown>,
  currentValues: Record<string, unknown>,
  form: UseFormReturn<Record<string, unknown>>,
  meta: Record<string, unknown>,
  getOptionProp: (field: string, prop: string) => unknown,
  customResolvers?: Record<string, (ctx: ConditionContext) => unknown>,
  precisionConfig?: FormPrecisionConfig,
): string[] {
  const written: string[] = [];
  if (!effect.set) return written;
  const watchNames = Array.isArray(effect.watch) ? effect.watch : [effect.watch];
  for (const [targetField, compute] of Object.entries(effect.set)) {
    const computeObj = compute as Compute;
    const needsLiveValues = computeObj.operator === 'appendUnique' || computeObj.operator === 'removeFromArray';
    const liveValues = form.getValues();
    const values = needsLiveValues ? liveValues : currentValues;

    let resolvedComputeObj = computeObj;
    if ((computeObj.operator === 'appendUnique' || computeObj.operator === 'removeFromArray') && computeObj.target !== targetField) {
      resolvedComputeObj = { ...computeObj, target: targetField };
    }

    const ctx = {
      fieldValue: watchMap[watchNames[0]],
      values,
      meta,
      getOptionProp,
      customResolvers,
    };
    const value = evaluateCompute(resolvedComputeObj, ctx);
    const normalized = normalizeProgrammaticNumericValue(value, targetField, precisionConfig);
    if (Object.is(getNestedValue(form.getValues() as Record<string, unknown>, targetField), normalized)) {
      continue;
    }
    form.setValue(targetField, normalized, { shouldDirty: false });
    written.push(targetField);
  }
  return written;
}

function applyCopy(
  copy: FormCopy | undefined,
  currentValues: Record<string, unknown>,
  form: UseFormReturn<Record<string, unknown>>,
): void {
  if (!copy) return;
  const { from, to, fields } = copy;
  for (const suffix of fields) {
    const sourceKey = `${from}.${suffix}`;
    const targetKey = `${to}.${suffix}`;
    const sourceValue = getNestedValue(currentValues, sourceKey);
    const cloned = sourceValue != null ? structuredClone(sourceValue) : null;
    form.setValue(targetKey, cloned, { shouldDirty: false });
  }
}

function clearCopyTargets(
  copy: FormCopy,
  form: UseFormReturn<Record<string, unknown>>,
): void {
  const { to, fields } = copy;
  for (const suffix of fields) {
    form.setValue(`${to}.${suffix}`, null, { shouldDirty: false });
  }
}

function applyMutualExclude(
  effect: FormEffect,
  watchMap: Record<string, unknown>,
  form: UseFormReturn<Record<string, unknown>>,
): void {
  const fields = effect.fields ?? [];
  if (fields.length === 0) return;

  const watchNames = Array.isArray(effect.watch) ? effect.watch : [effect.watch];
  const changedField = watchNames.find((name) => watchMap[name] != null && watchMap[name] !== '');
  if (!changedField) return;

  const changedValue = watchMap[changedField];
  if (changedValue == null || changedValue === '') return;

  for (const fieldPath of fields) {
    if (fieldPath === changedField) continue;
    const current = getNestedValue(form.getValues() as Record<string, unknown>, fieldPath);
    if (current != null && current === changedValue) {
      form.setValue(fieldPath, null, { shouldDirty: false });
    }
  }
}

function applyFormat(
  effect: FormEffect,
  watchMap: Record<string, unknown>,
  form: UseFormReturn<Record<string, unknown>>,
): void {
  if (!effect.format?.fn) return;
  const fn = FORMAT_FNS[effect.format.fn];
  if (!fn) return;
  const watchedScope = Array.isArray(effect.watch) ? effect.watch[0] : effect.watch;
  const current = String(watchMap[watchedScope] ?? '');
  if (current === '') return;
  const formatted = fn(current);
  if (formatted !== current) {
    form.setValue(watchedScope, formatted, { shouldDirty: true });
  }
}

function applyAppendIfEmpty(
  config: { field: string; value: unknown; template?: Record<string, unknown> },
  form: UseFormReturn<Record<string, unknown>>,
  currentValues: Record<string, unknown>,
  meta: Record<string, unknown>,
  getOptionProp: (field: string, prop: string) => unknown,
): void {
  const currentArray = form.getValues(config.field) as unknown[] | undefined;
  if (!currentArray || currentArray.length === 0) {
    let seedValue = config.value ?? {};
    if (config.template && typeof seedValue === 'object' && seedValue !== null) {
      const resolved: Record<string, unknown> = { ...(seedValue as Record<string, unknown>) };
      for (const [key, entry] of Object.entries(config.template)) {
        if (isCompute(entry)) {
          resolved[key] = evaluateCompute(entry as Compute, {
            fieldValue: null,
            values: currentValues,
            meta,
            getOptionProp,
          });
        } else {
          resolved[key] = entry;
        }
      }
      seedValue = resolved;
    }
    form.setValue(config.field, [seedValue], { shouldDirty: true });
  }
}

function isCompute(value: unknown): boolean {
  return value != null && typeof value === 'object' && 'operator' in (value as Record<string, unknown>);
}

function isEffectEnabled(
  effect: FormEffect,
  values: Record<string, unknown>,
): boolean {
  if (!effect.enabled) return true;
  const flag = getNestedValue(values, effect.enabled);
  return flag === true || flag === 'true' || (typeof flag === 'string' && flag.trim() !== '' && flag !== 'false');
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

function collectWatchFields(effects: FormEffect[]): string[] {
  const fields = new Set<string>();
  for (const effect of effects) {
    if (!effect.watch) continue;
    if (Array.isArray(effect.watch)) {
      effect.watch.forEach((f) => { if (f) fields.add(f); });
    } else if (typeof effect.watch === 'string') {
      fields.add(effect.watch);
    }
  }
  return Array.from(fields);
}
